import { AvisoConexao } from './AvisoConexao';
import { Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { captureException } from '@sentry/react';
import {
  FileSpreadsheet,
  Headset,
  HelpCircle,
  LogOut,
  Menu as IconeMais,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Sun,
} from 'lucide-react';
import { Alert, Button, cn, Dialog, EstadoVazio, LimiteErro, Select, Skeleton } from '@erp/ui';
import { api, quandoAssinaturaBloquear } from '../lib/api';
import { BuscaGlobal } from './BuscaGlobal';
import { Notificacoes } from './Notificacoes';
import { ContadorPendencias } from './Pendencias';
import { AssistenteSuporte } from './AssistenteSuporte';
import { InstalarApp } from './InstalarApp';
import { PesquisaNps, useRegistrarUso } from './Suporte';
import { NOME_PAPEL } from '../lib/formato';
import { menuDoPapel, ORDEM_GRUPOS, podeCadastrar, ROTULO_GRUPO, type ItemMenu } from '../lib/menu';
import { MenuNovo } from './MenuNovo';
import { useSessao } from '../lib/sessao';
import { alternarTema, temaAtual } from '../lib/tema';
import { AjudaFlutuante } from './AjudaFlutuante';

interface Aviso {
  id: string;
  titulo: string;
  mensagem: string;
  nivel: 'info' | 'atencao' | 'critico';
}

function lerRecolhido(): boolean {
  try {
    return localStorage.getItem('menu-recolhido') === '1';
  } catch {
    return false;
  }
}

/**
 * Computador: menu lateral recolhível, agrupado por área (cresce com as fases).
 * Celular: barra inferior com os principais + "Mais", e botão "+" para cadastrar carro.
 */
export function Layout() {
  const { usuario, filial, trocarFilial, sair } = useSessao();
  const { pathname } = useLocation();
  const [tema, setTema] = useState(temaAtual);
  const [recolhido, setRecolhido] = useState(lerRecolhido);
  const [mais, setMais] = useState(false);
  const [chamado, setChamado] = useState(false);
  useRegistrarUso();
  const [bloqueada, setBloqueada] = useState(false);
  useEffect(() => {
    quandoAssinaturaBloquear(() => setBloqueada(true));
    return () => {
      quandoAssinaturaBloquear(null);
    };
  }, []);
  const novidades = useQuery({
    queryKey: ['suporte', 'novidades'],
    queryFn: () => api<{ naoLidas: number }>('/suporte/novidades'),
    staleTime: 600_000,
  });
  const naoLidas = novidades.data?.naoLidas ?? 0;
  const avisos = useQuery({
    queryKey: ['avisos'],
    queryFn: () => api<Aviso[]>('/painel/avisos'),
    staleTime: 300_000,
  });
  if (!usuario) return null;

  const itens = menuDoPapel(usuario.papel, usuario.recursos);
  const celular = itens.filter((m) => m.celular).slice(0, 4);
  const multiloja = usuario.filiais.length > 1;
  const cadastra = podeCadastrar(usuario.papel);
  // No próprio cadastro o botão "+" some (a tela já tem o Salvar fixo).
  const mostrarMais = cadastra && pathname !== '/estoque/novo';

  const alternarMenu = () => {
    const novo = !recolhido;
    setRecolhido(novo);
    try {
      localStorage.setItem('menu-recolhido', novo ? '1' : '0');
    } catch {
      /* sem armazenamento */
    }
  };

  const seletorFilial = multiloja && (
    <Select
      aria-label="Filial"
      value={filial}
      onChange={(e) => trocarFilial(e.target.value)}
      className="h-9 w-full sm:w-auto sm:max-w-48"
    >
      <option value="todas">Todas as filiais</option>
      {usuario.filiais.map((f) => (
        <option key={f.id} value={f.id}>
          {f.nome}
        </option>
      ))}
    </Select>
  );

  const link = (m: ItemMenu, compacto: boolean) => (
    <NavLink
      key={m.rotulo}
      to={m.para}
      end={m.para === '/'}
      title={compacto ? m.rotulo : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-1.5 text-sm transition-colors',
          compacto && 'justify-center px-0',
          isActive
            ? 'bg-primary/10 font-medium text-primary'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        )
      }
    >
      <m.icone className="size-[18px] shrink-0" aria-hidden />
      <span className={cn(compacto && 'sr-only')}>{m.rotulo}</span>
      {m.para === '/ajuda' && naoLidas > 0 && (
        <span
          className={cn(
            'rounded-full bg-primary px-1.5 text-xs text-primary-foreground',
            compacto ? 'sr-only' : 'ml-auto',
          )}
        >
          {naoLidas}
          <span className="sr-only"> novidades</span>
        </span>
      )}
    </NavLink>
  );

  const grupos = ORDEM_GRUPOS.map((g) => ({ g, itens: itens.filter((m) => m.grupo === g) })).filter(
    (x) => x.itens.length,
  );
  // Ajuda vai para o topo (ícone com as novidades); o rodapé do menu fica só com Ajustes
  const rodape = itens.filter((m) => m.grupo === 'rodape' && m.para !== '/ajuda');

  return (
    <div className="min-h-dvh md:flex">
      {/* Menu lateral (computador) */}
      <aside
        className={cn(
          'sticky top-0 hidden h-dvh shrink-0 flex-col border-r bg-sidebar md:flex',
          recolhido ? 'w-16' : 'w-60',
        )}
      >
        <div
          className={cn(
            'flex h-14 items-center gap-2 border-b px-3',
            recolhido && 'justify-center',
          )}
        >
          <div
            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground"
            aria-hidden
          >
            {usuario.tenant.nome.slice(0, 1)}
          </div>
          {!recolhido && (
            <span className="min-w-0 flex-1 truncate font-semibold">{usuario.tenant.nome}</span>
          )}
          {!recolhido && (
            <button
              type="button"
              onClick={alternarMenu}
              className="rounded-md p-1 text-muted-foreground hover:bg-accent"
              aria-label="Recolher menu"
              title="Recolher menu"
            >
              <PanelLeftClose className="size-4" />
            </button>
          )}
        </div>
        <nav className="flex flex-1 flex-col gap-3 overflow-y-auto p-2" aria-label="Principal">
          {grupos.map(({ g, itens: lista }) => (
            <div key={g} className="flex flex-col gap-0.5">
              {ROTULO_GRUPO[g] && !recolhido && (
                <p className="px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                  {ROTULO_GRUPO[g]}
                </p>
              )}
              {lista.map((m) => link(m, recolhido))}
            </div>
          ))}
        </nav>
        <div
          className={cn(
            'flex flex-col gap-0.5 border-t p-2',
            rodape.length === 0 && !recolhido && 'hidden',
          )}
        >
          {rodape.map((m) => link(m, recolhido))}
          {recolhido && (
            <button
              type="button"
              onClick={alternarMenu}
              className="flex justify-center rounded-md py-1.5 text-muted-foreground hover:bg-accent"
              aria-label="Expandir menu"
              title="Expandir menu"
            >
              <PanelLeftOpen className="size-[18px]" />
            </button>
          )}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col pb-20 md:pb-0">
        {usuario.suporte.ativo && (
          <div
            role="status"
            className="flex items-center justify-center gap-2 bg-warning px-4 py-2 text-sm font-medium text-black"
          >
            <Headset className="size-4" aria-hidden />
            Sessão de suporte em andamento. Todas as ações ficam registradas.
          </div>
        )}
        <header className="sticky top-0 z-10 border-b bg-background/85 backdrop-blur">
          <div className="flex min-h-14 flex-wrap items-center gap-x-2 gap-y-0 px-4">
            <div className="flex h-14 min-w-0 flex-1 items-center gap-2 md:hidden">
              <div
                className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground"
                aria-hidden
              >
                {usuario.tenant.nome.slice(0, 1)}
              </div>
              <span className="truncate font-semibold">{usuario.tenant.nome}</span>
            </div>
            <div className="flex max-w-sm md:flex-1">
              <BuscaGlobal />
            </div>
            <div className="hidden h-14 flex-1 md:block" />
            {/* Celular: o seletor desce para uma linha própria, sem cortar o nome da loja */}
            {multiloja && (
              <div className="order-last w-full pb-2 sm:order-none sm:w-auto sm:pb-0">
                {seletorFilial}
              </div>
            )}
            <MenuNovo papel={usuario.papel} />
            <Button variant="ghost" size="icon" asChild className="relative hidden md:inline-flex">
              <Link to="/ajuda" aria-label="Ajuda" title="Ajuda e novidades">
                <HelpCircle />
                {naoLidas > 0 && (
                  <span className="absolute right-1 top-1 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                    {naoLidas}
                    <span className="sr-only"> novidades</span>
                  </span>
                )}
              </Link>
            </Button>
            <ContadorPendencias />
            <Notificacoes />
            <Button
              variant="ghost"
              size="icon"
              aria-label="Ajuda e suporte"
              title="Ajuda e suporte"
              onClick={() => setChamado(true)}
            >
              <Headset />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={tema === 'escuro' ? 'Usar tema claro' : 'Usar tema escuro'}
              title={tema === 'escuro' ? 'Usar tema claro' : 'Usar tema escuro'}
              onClick={() => setTema(alternarTema())}
              className="hidden md:inline-flex"
            >
              {tema === 'escuro' ? <Sun /> : <Moon />}
            </Button>
            <div className="hidden text-right text-xs leading-tight lg:block">
              <div className="font-medium">{usuario.nome}</div>
              <div className="text-muted-foreground">{NOME_PAPEL[usuario.papel]}</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Sair"
              title="Sair"
              onClick={() => void sair()}
              className="hidden md:inline-flex"
            >
              <LogOut />
            </Button>
          </div>
        </header>

        <main className="mx-auto flex w-full max-w-7xl flex-col gap-4 p-4 md:p-6">
          <AvisoConexao />
          {avisos.data?.map((a) => (
            <Alert
              key={a.id}
              variant={a.nivel === 'critico' ? 'erro' : a.nivel === 'atencao' ? 'atencao' : 'info'}
            >
              <div>
                <p className="font-medium">{a.titulo}</p>
                <p>{a.mensagem}</p>
              </div>
            </Alert>
          ))}
          {/* Pesquisa de satisfação só no Painel (sem dica de tela ali), nunca no meio do trabalho */}
          {pathname === '/' && <PesquisaNps />}
          {bloqueada && !pathname.startsWith('/ajustes/assinatura') ? (
            <EstadoVazio
              titulo="Acesso bloqueado por falta de pagamento"
              descricao={
                usuario.papel === 'dono' || usuario.papel === 'financeiro'
                  ? 'Seus dados, o site e os anúncios continuam funcionando. Pague a fatura e o painel volta na hora.'
                  : 'Seus dados continuam salvos. Fale com o dono da loja para regularizar a assinatura.'
              }
              acao={
                usuario.papel === 'dono' || usuario.papel === 'financeiro' ? (
                  <Button asChild onClick={() => setBloqueada(false)}>
                    <Link to="/ajustes/assinatura">Ver fatura</Link>
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <LimiteErro chave={pathname} aoErro={captureException}>
              <Suspense fallback={<Skeleton className="h-64" />}>
                <Outlet />
                <AjudaFlutuante />
              </Suspense>
            </LimiteErro>
          )}
        </main>
      </div>

      {/* Barra inferior (celular) */}
      <nav
        className="fixed inset-x-0 bottom-0 z-20 flex items-stretch border-t bg-card pb-[env(safe-area-inset-bottom)] md:hidden"
        aria-label="Principal (celular)"
      >
        {celular.slice(0, 2).map((m) => (
          <NavLink
            key={m.rotulo}
            to={m.para}
            end={m.para === '/'}
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center gap-1 py-2 text-xs',
                isActive ? 'text-primary' : 'text-muted-foreground',
              )
            }
          >
            <m.icone className="size-5" aria-hidden />
            {m.rotulo}
          </NavLink>
        ))}
        {mostrarMais && (
          <div className="flex flex-1 items-start justify-center">
            <Link
              to="/estoque/novo"
              aria-label="Cadastrar carro"
              className="-mt-5 flex size-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-4 ring-background"
            >
              <Plus className="size-7" />
            </Link>
          </div>
        )}
        {celular.slice(2).map((m) => (
          <NavLink
            key={m.rotulo}
            to={m.para}
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center gap-1 py-2 text-xs',
                isActive ? 'text-primary' : 'text-muted-foreground',
              )
            }
          >
            <m.icone className="size-5" aria-hidden />
            {m.rotulo}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setMais(true)}
          className="flex flex-1 flex-col items-center gap-1 py-2 text-xs text-muted-foreground"
        >
          <IconeMais className="size-5" aria-hidden />
          Mais
        </button>
      </nav>

      <AssistenteSuporte aberto={chamado} aoFechar={() => setChamado(false)} />

      <Dialog
        aberto={mais}
        aoFechar={() => setMais(false)}
        titulo={usuario.nome}
        descricao={NOME_PAPEL[usuario.papel]}
      >
        <nav
          className="flex flex-col gap-1"
          aria-label="Mais opções"
          onClick={() => setMais(false)}
        >
          {itens.filter((m) => !m.celular).map((m) => link(m, false))}
          {cadastra && (
            <Link
              to="/estoque/importar"
              className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-accent"
            >
              <FileSpreadsheet className="size-5" aria-hidden /> Importar planilha
            </Link>
          )}
          <InstalarApp />
          <button
            type="button"
            onClick={() => setTema(alternarTema())}
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent"
          >
            {tema === 'escuro' ? <Sun className="size-5" /> : <Moon className="size-5" />}{' '}
            {tema === 'escuro' ? 'Tema claro' : 'Tema escuro'}
          </button>
          <button
            type="button"
            onClick={() => void sair()}
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-destructive hover:bg-accent"
          >
            <LogOut className="size-5" /> Sair
          </button>
        </nav>
      </Dialog>
    </div>
  );
}
